import { Magnet, PhoneOutgoing, Sprout, type LucideIcon } from "lucide-react";
import type { z } from "zod";

import {
  coldCallHistorySchema,
  coldCallItemSchema,
  farmingItemSchema,
  leadHistorySchema,
  leadItemSchema,
  type ClientKind,
} from "@/features/clients/schemas";
import type { MessageId } from "@/shared/i18n/messages";

/* Everything that differs between leads, cold calls and farming, in one place. The screens are
   shared; they read the kind's endpoints, permissions and payload shapes from here. */

export interface ClientKindConfig {
  kind: ClientKind;
  /** Route and API base ("leads" → /leads and /api/leads). */
  path: string;
  api: string;
  icon: LucideIcon;
  titleId: MessageId;
  singularId: MessageId;
  createId: MessageId;
  itemSchema: z.ZodType;
  historySchema: z.ZodType | null;
  /** Status changes: method and path differ per kind. */
  statusMethod: "POST" | "PATCH";
  /** Assign body: the ids key and the agents key. */
  assign: { ids: string; agents: string };
  /** Import: the agents field name, or null when import assigns to the importer. */
  importAgents: string | null;
  canDelete: boolean;
  permission: {
    view: string;
    details: string;
    create: string;
    edit: string;
    assign: string;
    import: string;
    export: string;
    status: string;
    delete: string;
  };
}

function permissions(prefix: string) {
  return {
    view: `${prefix}.view`,
    details: `${prefix}.view.details`,
    create: `${prefix}.create`,
    edit: `${prefix}.edit`,
    assign: `${prefix}.assign`,
    import: `${prefix}.import`,
    export: `${prefix}.export`,
    status: `${prefix}.change.status`,
    delete: `${prefix}.delete`,
  };
}

export const CLIENT_KINDS: Readonly<Record<ClientKind, ClientKindConfig>> = {
  lead: {
    kind: "lead",
    path: "leads",
    api: "leads",
    icon: Magnet,
    titleId: "nav.leads",
    singularId: "clients.kind.lead",
    createId: "create.lead",
    itemSchema: leadItemSchema,
    historySchema: leadHistorySchema,
    statusMethod: "POST",
    assign: { ids: "lead_ids", agents: "assigned_to" },
    importAgents: "assigned_to",
    canDelete: true,
    permission: permissions("lead"),
  },
  "cold-call": {
    kind: "cold-call",
    path: "cold-calls",
    api: "cold-calls",
    icon: PhoneOutgoing,
    titleId: "nav.coldCalls",
    singularId: "clients.kind.coldCall",
    createId: "create.coldCall",
    itemSchema: coldCallItemSchema,
    historySchema: coldCallHistorySchema,
    statusMethod: "PATCH",
    assign: { ids: "cold_call_ids", agents: "agent_ids" },
    importAgents: "assigned_to",
    canDelete: false,
    permission: permissions("cold-call"),
  },
  farming: {
    kind: "farming",
    // Shown to people as "Resale"; the API and permissions keep the original name.
    path: "resale",
    api: "farming",
    icon: Sprout,
    titleId: "nav.farming",
    singularId: "clients.kind.farming",
    createId: "clients.create.farming",
    itemSchema: farmingItemSchema,
    historySchema: null,
    statusMethod: "POST",
    assign: { ids: "farming_ids", agents: "agent_ids" },
    importAgents: "assigned_agents",
    canDelete: false,
    // The farming assign endpoint has no permission of its own; editors may assign.
    permission: { ...permissions("farming"), assign: "farming.edit" },
  },
};

/** The history endpoint for kinds that have one (farming's comes with the record). */
export function historyPath(config: ClientKindConfig, id: number): string | null {
  if (config.kind === "lead") return `leads/${String(id)}/all-status-history`;
  if (config.kind === "cold-call") return `cold-calls/${String(id)}/full-status-history`;
  return null;
}
